export const TWELVE_FEES_ROW_TOOLTIP =
  'Facility fee is charged to the Twelve balance at first draw. Equal installments are due starting in the same month as cohort repayments (after the repayment grace period). XIRR fee accrues on the cohort portion of the Twelve balance only; unpaid facility fee does not accrue interest.';

function resolveMarketingGrace(calcResult, state) {
  if (calcResult?.marketingGrace != null) {
    return calcResult.marketingGrace;
  }
  return Math.max(0, Math.min(1, Number(state.marketingGracePeriod ?? 1)));
}

function marketingSpendInMonth(state, outputMonth, marketingGrace) {
  let sum = 0;
  for (let cohortIndex = 0; cohortIndex < state.marketing.length; cohortIndex += 1) {
    if (cohortIndex + marketingGrace === outputMonth) {
      sum += state.marketing[cohortIndex] || 0;
    }
  }
  return sum;
}

function resolveFundingPctInMonth(calcResult, state, outputMonth) {
  const cohortIndex = outputMonth - 1;
  if (cohortIndex < 0 || cohortIndex >= state.marketing.length) {
    return null;
  }
  const drawAmount = calcResult.cohortDraws[cohortIndex] || 0;
  if (drawAmount <= 0) {
    return null;
  }
  const marketing = Number(state.marketing[cohortIndex]) || 0;
  if (marketing <= 0) {
    return null;
  }
  return Math.min(100, (drawAmount * 100) / marketing);
}

function newCohortRevenueInMonth(calcResult, state, outputMonth) {
  let sum = 0;

  for (let cohortIndex = 0; cohortIndex < state.marketing.length; cohortIndex += 1) {
    if ((state.marketing[cohortIndex] || 0) <= 0) {
      continue;
    }
    sum += calcResult.cohortRevenue[cohortIndex]?.[outputMonth] || 0;
  }

  return sum;
}

function sumRepaymentsInMonth(calcResult, outputMonth) {
  return calcResult.cohortRepayments.reduce(
    (sum, row) => sum + (row[outputMonth] || 0),
    0
  );
}

function resolveFacilityFeeInMonth(calcResult, outputMonth) {
  const schedule = calcResult.facilityFeePaymentSchedule;
  if (!schedule || typeof schedule !== 'object') {
    const facilityFeeAmount = Number(calcResult.facilityFeeAmount) || 0;
    if (
      facilityFeeAmount <= 0 ||
      calcResult.firstDrawMonth !== outputMonth
    ) {
      return null;
    }
    return facilityFeeAmount;
  }
  const installment = schedule[outputMonth] ?? schedule[String(outputMonth)];
  if (installment == null || installment <= 0) {
    return null;
  }
  return installment;
}

function columnHasActivity(column) {
  return (
    (column.marketing ?? 0) < 0 ||
    column.roasDollar > 0 ||
    column.fundingPct != null ||
    column.twelveFunding != null ||
    column.repayment != null ||
    (column.twelveFees ?? 0) < 0
  );
}

function resolveLastActivityOutputMonth(calcResult, state, marketingGrace) {
  let lastMonth = 0;

  for (let outputMonth = 0; outputMonth < calcResult.N_OUT; outputMonth += 1) {
    const hasActivity =
      marketingSpendInMonth(state, outputMonth, marketingGrace) > 0 ||
      (calcResult.draw[outputMonth] || 0) > 0 ||
      sumRepaymentsInMonth(calcResult, outputMonth) > 0 ||
      newCohortRevenueInMonth(calcResult, state, outputMonth) > 0 ||
      resolveFacilityFeeInMonth(calcResult, outputMonth) != null;

    if (hasActivity) {
      lastMonth = outputMonth;
    }
  }

  return lastMonth;
}

/**
 * Compact cashflow table aligned with the P&L output months.
 * ROAS $ follows P&L revenue accrual (e.g. June spend cohort earns in June).
 * Marketing spend, Twelve funding, and facility fees follow cash timing (shifted
 * by marketing grace — e.g. June UA spend and funding land in July when grace
 * is net-30). Repayments stay in the month they occur.
 */
export function buildMarketingMonthCashflowTable(calcResult, state) {
  if (!calcResult || !state?.marketing?.length) {
    return null;
  }

  const marketingGrace = resolveMarketingGrace(calcResult, state);
  const lastOutputMonth = resolveLastActivityOutputMonth(
    calcResult,
    state,
    marketingGrace
  );

  const columns = [];

  for (let outputMonth = 0; outputMonth <= lastOutputMonth; outputMonth += 1) {
    const marketingSpend = marketingSpendInMonth(state, outputMonth, marketingGrace);
    const marketing = marketingSpend > 0 ? -marketingSpend : null;
    const twelveFundingAmount = calcResult.draw[outputMonth] || 0;
    const twelveFunding = twelveFundingAmount > 0 ? twelveFundingAmount : null;
    const fundingPct = resolveFundingPctInMonth(calcResult, state, outputMonth);
    const roasDollar = newCohortRevenueInMonth(calcResult, state, outputMonth);
    const repaymentTotal = sumRepaymentsInMonth(calcResult, outputMonth);
    const repayment = repaymentTotal > 0 ? -repaymentTotal : null;
    const twelveFeesAmount = resolveFacilityFeeInMonth(calcResult, outputMonth);
    const twelveFees =
      twelveFeesAmount != null ? -twelveFeesAmount : null;

    const column = {
      outputMonth,
      label: calcResult.cols[outputMonth],
      fundingPct,
      marketing,
      roasDollar,
      twelveFunding,
      repayment,
      twelveFees,
      netCashflow:
        roasDollar +
        (twelveFunding || 0) +
        (marketing || 0) +
        (twelveFees || 0) +
        (repayment || 0),
    };

    if (columnHasActivity(column)) {
      columns.push(column);
    }
  }

  if (columns.length === 0) {
    return null;
  }

  return {
    columns,
    rows: [
      { key: 'fundingPct', label: 'Funding %', format: 'pct' },
      { key: 'marketing', label: 'Marketing', format: 'money' },
      { key: 'roasDollar', label: 'ROAS $', format: 'money' },
      { key: 'twelveFunding', label: 'Twelve funding', format: 'money' },
      { key: 'repayment', label: 'Repayment', format: 'money' },
      {
        key: 'twelveFees',
        label: 'Twelve fees',
        format: 'money',
        tooltip: TWELVE_FEES_ROW_TOOLTIP,
      },
      {
        key: 'netCashflow',
        label: 'Net cashflow',
        format: 'money',
        highlight: true,
      },
    ],
  };
}
